import React from "react";
import OrderTab from "../../../enum/OrderTab";
import { observer } from "mobx-react-lite";
import ShopType from "@/enum/ShopType";
import SelectAllCheckbox from "./components/SelectAllCheckbox";
import { useModalStore } from "@/hooks/Modal/hooks";
import OrderTableColumnsType from "@/scenes/OrderManagement/enum/OrderTableColumnsType";
import ProductTableHead from "./components/ProductTableHead";
import ShowOrderDetailsSwitch from "../ListOptionsPane/ShowOrderDetailsSwitch";
import OrderTableColumnsTypeNameMap from "@/scenes/OrderManagement/map/OrderTableColumnsTypeNameMap";
import { getOrderTablePayTimeName, getOrderTablePurchaserName } from "../utils";
import ShopDTO from "@/dto/shop/ShopDTO";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import StatusHead from "./components/StatusHead";
import { Tooltip } from "antd";
import BuyerSearchWaybillModal from "@/scenes/setting/Shop/ListPage/components/BuyerSearchWaybillModal";
import { InfoCircleOutlined } from "@ant-design/icons";
import OriginalIdHead from "./components/OriginalIdHead";
import { getRenderableOrderTableColumns } from "../utils";
import ResizableTableHead from "./components/ResizableTableHead";

interface OrderTableHeadProps {
    tab?: OrderTab;

    shop?: ShopDTO;
}

const OrderTableHead: React.FC<OrderTableHeadProps> = function (props) {
    const { tab, shop } = props;

    const modalStore = useModalStore();
    const orderManagementStore = useOrderManagementStore();

    const ths: React.ReactNode[] = [];
    const renderableColumns = getRenderableOrderTableColumns(orderManagementStore.storage.sortedColumns, tab);
    const consigneeColumnIndex = renderableColumns.findIndex(({ type }) => type === OrderTableColumnsType.CONSIGNEE);

    const getResizeHandleSide = (columnType: OrderTableColumnsType) => {
        const columnIndex = renderableColumns.findIndex(({ type }) => type === columnType);

        return consigneeColumnIndex >= 0 && consigneeColumnIndex < columnIndex ? "left" : "right";
    };

    renderableColumns.forEach((column) => {
        const { type: key } = column;

        switch (key) {
            case OrderTableColumnsType.CHECKBOX:
                ths.push(
                    <th key={OrderTableColumnsType.CHECKBOX} style={{ width: 25, textAlign: "center" }}>
                        <SelectAllCheckbox />
                    </th>,
                );
                break;

            case OrderTableColumnsType.STATUS:
                ths.push(<StatusHead key={OrderTableColumnsType.STATUS} />);
                break;

            case OrderTableColumnsType.ORIGINAL_ID:
                ths.push(<OriginalIdHead key={OrderTableColumnsType.ORIGINAL_ID} shop={shop} tab={tab} />);
                break;

            case OrderTableColumnsType.RETAILER:
                ths.push(
                    <th key={OrderTableColumnsType.RETAILER}>
                        {OrderTableColumnsTypeNameMap[OrderTableColumnsType.RETAILER]}
                    </th>,
                );
                break;
            case OrderTableColumnsType.BUYER_USERNAME:
                ths.push(<th key={OrderTableColumnsType.BUYER_USERNAME}>{getOrderTablePurchaserName(shop.type)}</th>);
                break;

            case OrderTableColumnsType.PAYMENTS:
                ths.push(
                    <th key={OrderTableColumnsType.PAYMENTS}>
                        {OrderTableColumnsTypeNameMap[OrderTableColumnsType.PAYMENTS]}
                    </th>,
                );
                break;

            case OrderTableColumnsType.QUANTITY:
                ths.push(
                    <th key={OrderTableColumnsType.QUANTITY}>
                        {OrderTableColumnsTypeNameMap[OrderTableColumnsType.QUANTITY]}
                    </th>,
                );
                break;

            case OrderTableColumnsType.SELLER_RECEIPTS:
                ths.push(
                    <th key={OrderTableColumnsType.SELLER_RECEIPTS}>
                        {OrderTableColumnsTypeNameMap[OrderTableColumnsType.SELLER_RECEIPTS]}
                    </th>,
                );
                break;

            case OrderTableColumnsType.PRODUCT:
                ths.push(
                    <ProductTableHead
                        key={OrderTableColumnsType.PRODUCT}
                        shop={shop}
                        resizeHandleSide={getResizeHandleSide(OrderTableColumnsType.PRODUCT)}
                    />,
                );
                break;

            case OrderTableColumnsType.CUSTOMIZATION_INFO:
                ths.push(
                    <th key={OrderTableColumnsType.CUSTOMIZATION_INFO}>
                        {OrderTableColumnsTypeNameMap[OrderTableColumnsType.CUSTOMIZATION_INFO]}
                    </th>,
                );
                break;
            case OrderTableColumnsType.SHIPPING_DEADLINE:
                ths.push(
                    <th key={OrderTableColumnsType.SHIPPING_DEADLINE}>
                        {OrderTableColumnsTypeNameMap[OrderTableColumnsType.SHIPPING_DEADLINE]}
                    </th>,
                );
                break;

            case OrderTableColumnsType.ORDER_TIME:
                ths.push(
                    <th key={OrderTableColumnsType.ORDER_TIME}>
                        {OrderTableColumnsTypeNameMap[OrderTableColumnsType.ORDER_TIME]}
                    </th>,
                );
                break;
            case OrderTableColumnsType.PAYMENT_TIME:
                ths.push(<th key={OrderTableColumnsType.PAYMENT_TIME}>{getOrderTablePayTimeName(shop.type)}</th>);
                break;
            case OrderTableColumnsType.INVOICE_INFO:
                ths.push(
                    <th key={OrderTableColumnsType.INVOICE_INFO}>
                        {OrderTableColumnsTypeNameMap[OrderTableColumnsType.INVOICE_INFO]}
                    </th>,
                );
                break;
            case OrderTableColumnsType.CONSIGNEE:
                ths.push(
                    <th key={OrderTableColumnsType.CONSIGNEE}>
                        <span>收货人信息</span>
                    </th>,
                );
                break;

            case OrderTableColumnsType.ORDER_REMARK:
                ths.push(
                    <ResizableTableHead
                        key={OrderTableColumnsType.ORDER_REMARK}
                        columnType={OrderTableColumnsType.ORDER_REMARK}
                        defaultWidth={160}
                        resizeHandleSide={getResizeHandleSide(OrderTableColumnsType.ORDER_REMARK)}
                    >
                        <span>{OrderTableColumnsTypeNameMap[OrderTableColumnsType.ORDER_REMARK]}</span>
                    </ResizableTableHead>,
                );
                break;
            case OrderTableColumnsType.SHIPPING_TIME:
                ths.push(
                    <th key={OrderTableColumnsType.SHIPPING_TIME}>
                        {tab === OrderTab.PRE_SHIP ? "预发货时间" : "发货时间"}
                    </th>,
                );
                break;

            case OrderTableColumnsType.WAYBILL_NO:
                ths.push(
                    <th key={OrderTableColumnsType.WAYBILL_NO}>
                        {OrderTableColumnsTypeNameMap[OrderTableColumnsType.WAYBILL_NO]}

                        {[ShopType.MANUAL, ShopType.INDEPENDENT_MANUAL].includes(shop.type) ? (
                            <a
                                className="ml5 text-small"
                                href="#"
                                onClick={() => {
                                    modalStore.openModal(BuyerSearchWaybillModal, {
                                        shop,
                                    });
                                }}
                            >
                                买家查运单号链接
                            </a>
                        ) : null}
                    </th>,
                );
                break;
            case OrderTableColumnsType.SUPPLIER_DROPSHIPPING2:
                ths.push(
                    <th key={OrderTableColumnsType.SUPPLIER_DROPSHIPPING2}>
                        {OrderTableColumnsTypeNameMap[OrderTableColumnsType.SUPPLIER_DROPSHIPPING2]}
                    </th>,
                );
                break;
            case OrderTableColumnsType.SEQUENCE_NO:
                ths.push(
                    <th key={OrderTableColumnsType.SEQUENCE_NO}>
                        {OrderTableColumnsTypeNameMap[OrderTableColumnsType.SEQUENCE_NO]}
                    </th>,
                );
                break;
            case OrderTableColumnsType.WEIGHT:
                ths.push(<th key={OrderTableColumnsType.WEIGHT}>重量</th>);
                break;
            case OrderTableColumnsType.SHIPPER:
                ths.push(
                    <th key={OrderTableColumnsType.SHIPPER}>
                        {OrderTableColumnsTypeNameMap[OrderTableColumnsType.SHIPPER]}
                    </th>,
                );
                break;
            case OrderTableColumnsType.LOGISTICS_AGING_PREDICT:
                ths.push(
                    <th key={OrderTableColumnsType.LOGISTICS_AGING_PREDICT}>
                        {OrderTableColumnsTypeNameMap[OrderTableColumnsType.LOGISTICS_AGING_PREDICT]}
                    </th>,
                );
                break;

            case OrderTableColumnsType.LOGISTICS_RECOMMEND:
                ths.push(
                    <th key={OrderTableColumnsType.LOGISTICS_RECOMMEND}>
                        {OrderTableColumnsTypeNameMap[OrderTableColumnsType.LOGISTICS_RECOMMEND]}
                        <Tooltip title="平台提供">
                            <InfoCircleOutlined className="ml3" />
                        </Tooltip>
                    </th>,
                );
                break;

            case OrderTableColumnsType.QUICK_OPERATION:
                ths.push(
                    <th key={OrderTableColumnsType.QUICK_OPERATION}>
                        {OrderTableColumnsTypeNameMap[OrderTableColumnsType.QUICK_OPERATION]}
                    </th>,
                );
                break;

            case OrderTableColumnsType.OPERATION:
                ths.push(
                    <th key={OrderTableColumnsType.OPERATION}>
                        <ShowOrderDetailsSwitch />
                    </th>,
                );
                break;

            default:
                break;
        }
    });

    return (
        <thead>
            <tr>{ths}</tr>
        </thead>
    );
};

export default observer(OrderTableHead);
